import { DownloadOutlined, InfoOutlined } from "@mui/icons-material";
import { Box, Button, Card, CardContent, CircularProgress, Divider, FormControl, IconButton, InputLabel, MenuItem, Select, Tab, Tabs, TextField, Tooltip, Typography } from "@mui/material";
import { generateClient } from "aws-amplify/api";
import moment from "moment";
import { useContext, useEffect, useRef, useState } from "react";
import { UserContext } from "../../contexts/UserContext";
import { esQuery } from "../../graphql/queries";
import { actionOneButtonStyle, cardStyle } from "../../theme";
import CampaignCallDetailsDialog from "./campaignCallDetails";
import CampaignCallSuppressDialog from "./campaignSuppressionDetails";
import PenetrationDialog from "./penetrationDialog";
import { downloadSMS } from "src/tools/downloadSMS";


function CampaignHistory({ campaigns }) {
	const client = generateClient();
	const userContext = useContext(UserContext);
	const [selCampaign, setSelCampaign] = useState(null);
	const selCampaignRef = useRef(null);
	const [selStartDate, setSelStartDate] = useState(new Date().toISOString().split('T')[0]);
	const [selEndDate, setSelEndDate] = useState(new Date().toISOString().split('T')[0]);
	const selStartDateRef = useRef(new Date().toISOString().split('T')[0]);
	const selEndDateRef = useRef(new Date().toISOString().split('T')[0]);

	const [showSMS, setShowSMS] = useState(false);
	const showSMSRef = useRef(false);
	const [smsSentCount, setSmsSentCount] = useState(0);
	const [smsFailedCount, setSmsFailedCount] = useState(0);

	const [queryCampaign, setQueryCampaign] = useState(null);
	const [queryStartDate, setQueryStartDate] = useState(null);
	const [queryEndDate, setQueryEndDate] = useState(null);

	const [queryCampaignCallData, setQueryCampaignCallData] = useState(null);
	const [queryCampaignSuppressionData, setQueryCampaignSuppressionData] = useState(null);
	const [suppressCount, setSuppressCount] = useState(0);

	const [currentTab, setCurrentTab] = useState(0);
	const [loading, setLoading] = useState(false);

	function setQueryDetails() {
		if (selCampaignRef.current !== null && selStartDateRef.current <= selEndDateRef.current) {
			setQueryCampaign(selCampaignRef.current);
			setQueryStartDate(moment(selStartDateRef.current).startOf('day').toISOString());
			setQueryEndDate(moment(selEndDateRef.current).endOf('day').toISOString());
		}
	}

	useEffect(() => {
		if (queryCampaign && queryStartDate && queryEndDate) {
			getCampaignCallHistory(queryCampaign.id);
		}
	}, [queryCampaign, queryStartDate, queryEndDate]);

	async function getCampaignCallHistory(campaignId) {
		try {
			setLoading(true);

			// Check if campaign uses SMS channel
			const usesSMS = queryCampaign.profile.channels.find(channel => channel.type === "SMS");
			setShowSMS(usesSMS ? true : false);
			showSMSRef.current = usesSMS ? true : false;

			let promises = [];
			promises.push(client.graphql({
				query: esQuery,
				variables: {
					model: 'activitylog-*',
					action: '_search',
					query: JSON.stringify(getCallHistoryQuery(campaignId, queryStartDate, queryEndDate)),
				},
			}));
			promises.push(client.graphql({
				query: esQuery,
				variables: {
					model: 'activitylog-*',
					action: '_search',
					query: JSON.stringify(getCallsSuppressedQuery(campaignId, queryStartDate, queryEndDate, "metaData.dispDescription.keyword")),
				},
			}));
			promises.push(client.graphql({
				query: esQuery,
				variables: {
					model: 'activitylog-*',
					action: '_search',
					query: JSON.stringify(getCallsSuppressedQuery(campaignId, queryStartDate, queryEndDate, "metaData.DispositionDescription.keyword")),
				},
			}));
			if (showSMSRef.current) {
				promises.push(client.graphql({
					query: esQuery,
					variables: {
						model: 'activitylog-*',
						action: '_search',
						query: JSON.stringify(getSMSHistoryQuery(campaignId, queryStartDate, queryEndDate)),
					},
				}));
			}

			const data = await Promise.all(promises);
			const calls = JSON.parse(data[0].data.esQuery);
			const suppressedOne = JSON.parse(data[1].data.esQuery);
			const suppressedTwo = JSON.parse(data[2].data.esQuery);

			setQueryCampaignCallData(calls.aggregations.status.buckets);
			setQueryCampaignSuppressionData([...suppressedOne.aggregations.suppressions.buckets, ...suppressedTwo.aggregations.suppressions.buckets]);
			setSuppressCount(suppressedOne.hits.total.value)

			if (showSMSRef.current) {
				const smsData = JSON.parse(data[3].data.esQuery);
				let sentCount = 0;
				let failedCount = 0;
				for (const bucket of smsData.aggregations.status.buckets) {
					if (bucket.key === 'SMS Sent') {
						sentCount = bucket.doc_count;
					} else if (bucket.key === 'SMS Failed') {
						failedCount = bucket.doc_count;
					}
				}
				setSmsSentCount(sentCount);
				setSmsFailedCount(failedCount);
			}

		} catch (error) {
			console.error(error);
		}
		setLoading(false);
	}

	function getCallHistoryQuery(campaignId, startTime, endTime) {
		return {
			"aggs": {
				"status": {
					"terms": {
						"field": "contactStatus.keyword",
						"size": 2147483647
					},
					"aggs": {
						"disposition": {
							"terms": {
								"field": "disposition.keyword",
								"size": 2147483647
							},
							"aggs": {
								"agent": {
									"terms": {
										"field": "agent.keyword",
										"size": 2147483647
									}
								}
							}
						}
					}
				}
			},
			"query": {
				"bool": {
					"must": [
						{
							"term": {
								"tenant.keyword": userContext.tenantId
							}
						},
						{
							"match_phrase": {
								"type": "Dial"
							}
						},
						{
							"match_phrase": {
								"activityLogCampaignId": campaignId
							}
						},
						{
							"range": {
								"createdAt": {
									"gte": startTime,
									"lte": endTime
								}
							}
						}
					],
					"must_not": [
						{
							"match_phrase": {
								"disposition": "Cadence Exhaustion - Retry Increment"
							}
						}
					]
				}
			},
			"track_total_hits": true,
			"size": 0
		}
	}

	function getCallsSuppressedQuery(campaignId, startTime, endTime, aggField) {
		return {
			"aggs": {
				"suppressions": {
					"terms": {
						"field": aggField,
						"size": 2147483647
					}
				}
			},
			"query": {
				"bool": {
					"must": [
						{
							"term": {
								"tenant.keyword": userContext.tenantId
							}
						},
						{
							"match_phrase": {
								"type": "DialSuppress"
							}
						},
						{
							"match_phrase": {
								"activityLogCampaignId": campaignId
							}
						},
						{
							"range": {
								"createdAt": {
									"gte": startTime,
									"lte": endTime
								}
							}
						}
					],
					"must_not": [
						{
							"match_phrase": {
								"metaData.dispDescription": "The contact was outside the specified curfew bounds and will be retried."
							}
						},
						{
							"match_phrase": {
								"metaData.dispDescription": "The contact was outside the specified call window bounds for the point of contact."
							}
						}
					]
				}
			},
			"track_total_hits": true,
			"size": 0
		}
	}

	function getSMSHistoryQuery(campaignId, startTime, endTime) {
		return {
			"aggs": {
				"status": {
					"terms": {
						"field": "title.keyword",
						"size": 2147483647
					}
				}
			},
			"query": {
				"bool": {
					"must": [
						{
							"term": {
								"tenant.keyword": userContext.tenantId
							}
						},
						{
							"match_phrase": {
								"type": "SMS"
							}
						},
						{
							"match_phrase": {
								"activityLogCampaignId": campaignId
							}
						},
						{
							"range": {
								"createdAt": {
									"gte": startTime,
									"lte": endTime
								}
							}
						}
					]
				}
			},
			"track_total_hits": true,
			"size": 0
		}
	}



	return (
		<Card variant="outlined" sx={cardStyle}>
			<CardContent>
				<Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" gap={2} width="100%" height="100%">
					<Box display="grid" gridTemplateColumns="9fr 3fr 3fr 3fr .5fr" gap={2} alignItems="center" width="100%">
						<FormControl>
							<InputLabel id="campaign-select-label">Select Campaign</InputLabel>
							<Select
								labelId="campaign-select-label"
								id="campaign-select"
								value={selCampaign}
								label="Select Campaign"
								onChange={(e) => {
									setSelCampaign(e.target.value)
									selCampaignRef.current = e.target.value;
								}}
							>
								{campaigns.map((campaign) => (
									<MenuItem key={campaign.id} value={campaign}>
										{campaign.name}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<FormControl>
							<TextField
								id="date"
								label="Select Start Date"
								type="date"
								// Min Date is 90 days ago from today
								// Max Date is today
								inputProps={{
									min: moment().subtract(90, 'days').format('YYYY-MM-DD'),
									max: moment().format('YYYY-MM-DD')
								}}
								value={selStartDate}
								onChange={(e) => {
									const selectedDate = moment(e.target.value).startOf('day');
									setSelStartDate(e.target.value)
									selStartDateRef.current = e.target.value;

									// If End Date is more than 7 days from Start Date, set it to 7 days from Start Date
									const maxEndDate = moment(e.target.value).add(6, 'days').format('YYYY-MM-DD');
									if (selEndDateRef.current > maxEndDate) {
										setSelEndDate(maxEndDate);
										selEndDateRef.current = maxEndDate;
									}

									// If End Date is before Start Date, set it to Start Date
									if (selEndDateRef.current < e.target.value) {
										setSelEndDate(e.target.value);
										selEndDateRef.current = e.target.value;
									}
								}}
								InputLabelProps={{
									shrink: true,
								}}
							/>
						</FormControl>
						<FormControl>
							<TextField
								id="date"
								label="Select End Date"
								type="date"
								// Must be within 7 days of start date
								inputProps={{
									min: moment(selStartDate).format('YYYY-MM-DD'),
									max: moment(selStartDate).add(6, 'days').format('YYYY-MM-DD')
								}}
								value={selEndDate}
								onChange={(e) => {
									setSelEndDate(e.target.value)
									selEndDateRef.current = e.target.value;
								}}
								InputLabelProps={{
									shrink: true,
								}}
							/>
						</FormControl>
						<Button onClick={setQueryDetails} sx={actionOneButtonStyle}>Query</Button>
						<Tooltip title="Query Data from the last 90 days. You can view aggregated data up to 7 days.">
							<InfoOutlined color="primary" />
						</Tooltip>
					</Box>
					{queryCampaignCallData && queryCampaignSuppressionData && <Box display="flex" flexDirection="column" gap={2} width="100%">
						<Box display="flex" flexDirection="column" gap={2} alignItems="center" justifyContent="center">
							<Typography color="primary" variant="body1">{queryStartDate} - {queryEndDate}</Typography>
							<Typography variant="h2">{queryCampaign.name}</Typography>
							{!loading && <>
								<Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)}>
									<Tab label="Penetration" onClick={() => setCurrentTab(0)} />
									<Tab label="Call Results" onClick={() => setCurrentTab(1)} />
									{showSMS && <Tab label="SMS" onClick={() => setCurrentTab(2)} />}
									<Tab label="Suppressions" onClick={() => setCurrentTab(3)} />
								</Tabs>

								{currentTab === 0 && <PenetrationDialog
									open={true}
									campaignId={queryCampaign.id}
									inputStartDate={queryStartDate}
									inputEndDate={queryEndDate}
									suppressCount={suppressCount}
								/>}
								{currentTab === 1 &&
									<CampaignCallDetailsDialog
										open={true}
										data={queryCampaignCallData}
										campaignId={queryCampaign.id}
										inputStartDate={queryStartDate}
										inputEndDate={queryEndDate}
										segments={JSON.parse(queryCampaign.segments)}
										campaignName={queryCampaign.name}
									/>
								}
								{currentTab === 2 && showSMS &&
									<Card variant="outlined" sx={cardStyle}>
										<CardContent>
											<Box display="flex" flexDirection="column" gap={2} alignItems="center" justifyContent="center">
												<Typography variant="h5">SMS Summary</Typography>
												<Box display="flex" alignItems="center" gap={4}>
												<Box display="flex" flexDirection="column" alignItems="center" >
													<Box display="flex" alignItems="center" gap={1}>
														<Typography variant="h2">{smsSentCount}</Typography>
														<IconButton onClick={() => downloadSMS('SMS Sent', queryCampaign.id, userContext.tenantId, queryStartDate, queryEndDate)}><DownloadOutlined color="primary" /></IconButton>
													</Box>
													<Box display="flex" flexDirection="column" alignItems="center">
														<Typography variant="h6">SMS Sent</Typography>
													</Box>
												</Box>
												<Divider orientation="vertical" flexItem />
												<Box display="flex" flexDirection="column" alignItems="center">
													<Box display="flex" alignItems="center" gap={1}>
														<Typography variant="h2">{smsFailedCount}</Typography>
														<IconButton onClick={() => downloadSMS('SMS Failed', queryCampaign.id, userContext.tenantId, queryStartDate, queryEndDate)}><DownloadOutlined color="primary" /></IconButton>
													</Box>
													<Box display="flex" flexDirection="column" alignItems="center">
														<Typography variant="h6">SMS Failed</Typography>
													</Box>
												</Box>
												</Box>
											</Box>
										</CardContent>
									</Card>}
								{currentTab === 3 && <CampaignCallSuppressDialog
									open={true}
									data={queryCampaignSuppressionData}
									campaignId={queryCampaign.id}
									inputStartDate={queryStartDate}
									inputEndDate={queryEndDate}
								/>}
							</>}
							{loading && <Box display="flex" height="100%" alignItems="center" justifyContent="center">
								<CircularProgress size={50} color="primary" />
							</Box>}
						</Box>
					</Box>}

				</Box>
			</CardContent>
		</Card>)
}

export default CampaignHistory;